import { Checkbox } from "antd";
import {
  TEnrichedActivity,
  TFeatureActivityWithInstance
} from "components/feature-entity-linking-page/models";
import { TLinkRecommenderRecommendation } from "pages/bulk-linking/hooks/ai-suggested-activities";
import {
  EMPTY_FILTERS,
  matchesScheduleFilters,
  type FilterState
} from "components/schedule-hierarchy/schedule-filter-bar";
import { DateUtils } from "utils/dateutils";

type SelectedActivityLink = {
  id: string;
  isLinkedWithStartdate: boolean;
};

function getMatchConfidenceLabel(
  value?: string | null
): "High" | "Medium" | "Low" | null {
  if (!value) return null;
  const normalized = value.trim().toLowerCase();
  if (normalized === "high") return "High";
  if (normalized === "medium") return "Medium";
  if (normalized === "low") return "Low";
  return null;
}

export function getRecommendedDateLink(
  phase?: string | null
): "start" | "end" | null {
  const normalized = (phase || "").trim().toLowerCase().replace(/[-\s]/g, "");
  if (normalized === "preconstruction") return "end";
  if (normalized === "construction") return "start";
  return null;
}

function DateLinkCheckbox({
  isStartDate,
  activity,
  selectedIds,
  isWBSLinkingEnabled,
  isSuggested,
  onChange
}: {
  isStartDate: boolean;
  activity: TFeatureActivityWithInstance | undefined;
  selectedIds: SelectedActivityLink[];
  isWBSLinkingEnabled: boolean;
  isSuggested?: boolean;
  onChange: (
    isStartDate: boolean,
    checked: boolean,
    activity: TFeatureActivityWithInstance
  ) => void;
}) {
  if (!activity) return null;

  const date = isStartDate ? activity.start_date : activity.end_date;
  if (!date) return null;

  const checked = selectedIds.some((item) =>
    isStartDate
      ? item.id === activity.feature_instance_id && item.isLinkedWithStartdate
      : item.id === activity.feature_instance_id && !item.isLinkedWithStartdate
  );

  const showCheckbox =
    activity.type === 5 ? isWBSLinkingEnabled || checked : true;

  const formattedDate = DateUtils.format(date, "MM-DD-YYYY");
  const label = isStartDate ? "Start Date" : "End Date";
  const testId = isStartDate
    ? `ai-recommendation-start-checkbox-${activity.feature_instance_id}`
    : `ai-recommendation-end-checkbox-${activity.feature_instance_id}`;

  return (
    <label className={`ai-card-date-opt${isSuggested ? " is-suggested" : ""}`}>
      {showCheckbox ? (
        <Checkbox
          checked={checked}
          onChange={(event) =>
            onChange(isStartDate, event.target.checked, activity)
          }
          data-testid={testId}
        >
          <span className="ai-card-date-label">{label}</span>
          <span className={`ai-card-date-val${checked ? " is-checked" : ""}`}>
            {formattedDate}
          </span>
        </Checkbox>
      ) : (
        <>
          <span className="ai-card-date-label">{label}</span>
          <span className="ai-card-date-val">{formattedDate}</span>
        </>
      )}
      {isSuggested ? (
        <span
          className="ai-card-date-suggested"
          data-testid={`${testId}-suggested`}
        >
          Suggested
        </span>
      ) : null}
    </label>
  );
}

export function getAiRecommendationFilterRow(
  recommendation: TLinkRecommenderRecommendation,
  activity?: TEnrichedActivity
) {
  return {
    phase: activity?.phase || recommendation.phase || "",
    csi_division: activity?.csi_division || recommendation.csi_div || "",
    activity_type:
      activity?.activity_type || recommendation.activity_type || "",
    location_tags: activity?.location_tags || recommendation.location || ""
  };
}

export function filterAiRecommendations(
  recommendations: TLinkRecommenderRecommendation[] | undefined,
  props: {
    searchText: string;
    filters?: FilterState;
    activitiesById: Map<string, TFeatureActivityWithInstance>;
  }
) {
  const { searchText, filters = EMPTY_FILTERS, activitiesById } = props;
  const query = searchText.trim().toLowerCase();

  return (recommendations || []).filter((item) => {
    const activity = activitiesById.get(item.activity_feature_instance_id) as
      | TEnrichedActivity
      | undefined;
    if (
      !matchesScheduleFilters(
        getAiRecommendationFilterRow(item, activity),
        filters
      )
    ) {
      return false;
    }
    if (!query) return true;
    return [
      item.activity_name,
      item.activity_wbs_path,
      item.reasoning,
      item.predicted_material_name,
      activity?.csi_division || item.csi_div,
      activity?.activity_type || item.activity_type
    ]
      .filter(Boolean)
      .some((value) => String(value).toLowerCase().includes(query));
  });
}

export default function AIRecommendationCards({
  recommendations,
  loading,
  searchText,
  filters = EMPTY_FILTERS,
  showTags = false,
  activitiesById,
  selectedIds,
  isWBSLinkingEnabled,
  onChangeCheckbox
}: {
  recommendations: TLinkRecommenderRecommendation[] | undefined;
  loading: boolean;
  searchText: string;
  filters?: FilterState;
  showTags?: boolean;
  activitiesById: Map<string, TFeatureActivityWithInstance>;
  selectedIds: SelectedActivityLink[];
  isWBSLinkingEnabled: boolean;
  onChangeCheckbox: (
    isStartDate: boolean,
    checked: boolean,
    activity: TFeatureActivityWithInstance
  ) => void;
}) {
  const visibleRecommendations = filterAiRecommendations(recommendations, {
    searchText,
    filters,
    activitiesById
  });

  if (loading) {
    return (
      <div
        className="ai-recommendation-panel"
        data-testid="ai-recommendation-panel"
      >
        <div className="ai-recommendation-empty">Loading..</div>
      </div>
    );
  }

  if (!visibleRecommendations.length) {
    return (
      <div
        className="ai-recommendation-panel"
        data-testid="ai-recommendation-panel"
      >
        <div className="ai-recommendation-empty">No AI recommendations.</div>
      </div>
    );
  }

  return (
    <div
      className="ai-recommendation-panel"
      data-testid="ai-recommendation-panel"
    >
      {visibleRecommendations.map((recommendation) => {
        const activityId = recommendation.activity_feature_instance_id;
        const activity = activitiesById.get(activityId) as
          | TEnrichedActivity
          | undefined;
        const isLinked = selectedIds.some((item) => item.id === activityId);
        const linkedSide = selectedIds.find((item) => item.id === activityId);
        const matchConfidence = getMatchConfidenceLabel(
          recommendation.match_confidence
        );
        const tagRow = getAiRecommendationFilterRow(recommendation, activity);
        const recommendedDate = getRecommendedDateLink(tagRow.phase);

        return (
          <div
            key={recommendation.id || activityId}
            className={`ai-card${isLinked ? " is-linked" : ""}`}
            data-testid={`ai-recommendation-card-${activityId}`}
          >
            <div className="ai-card-header">
              <div className="ai-card-name">{recommendation.activity_name}</div>
              {matchConfidence ? (
                <span
                  className={`ai-card-conf ai-card-conf-${matchConfidence}`}
                  data-testid={`ai-recommendation-confidence-${activityId}`}
                >
                  {matchConfidence}
                </span>
              ) : null}
            </div>
            <div className="ai-card-path">
              {recommendation.activity_wbs_path || "—"}
            </div>
            {recommendation.reasoning ? (
              <div className="ai-card-why">
                <div className="ai-card-why-label">Why AI recommended</div>
                {recommendation.reasoning}
              </div>
            ) : null}
            {showTags && (tagRow.csi_division || tagRow.activity_type) ? (
              <div
                className="ai-card-meta"
                data-testid={`ai-recommendation-tags-${activityId}`}
              >
                {tagRow.csi_division ? (
                  <span className="ai-card-chip-csi">
                    {tagRow.csi_division}
                  </span>
                ) : null}
                {tagRow.activity_type ? (
                  <span className="ai-card-chip-type">
                    {tagRow.activity_type}
                  </span>
                ) : null}
              </div>
            ) : null}
            <div className="ai-card-date-row">
              <DateLinkCheckbox
                isStartDate
                activity={activity}
                selectedIds={selectedIds}
                isWBSLinkingEnabled={isWBSLinkingEnabled}
                isSuggested={recommendedDate === "start"}
                onChange={onChangeCheckbox}
              />
              <DateLinkCheckbox
                isStartDate={false}
                activity={activity}
                selectedIds={selectedIds}
                isWBSLinkingEnabled={isWBSLinkingEnabled}
                isSuggested={recommendedDate === "end"}
                onChange={onChangeCheckbox}
              />
              {isLinked ? (
                <span className="ai-card-linked-badge">
                  Linked · {linkedSide?.isLinkedWithStartdate ? "Start" : "End"}
                </span>
              ) : null}
            </div>
          </div>
        );
      })}
    </div>
  );
}
